// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.
// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { useEffect, useState } from 'react'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { LineChartPro } from '@mui/x-charts-pro/LineChartPro'
import dayjs from 'dayjs'
import { JsonRpcProvider } from 'ethers'
import { fetchLytHistory, LytHistoryCTO, LytHistoryPeriod } from './LytFetchers'
import { logOT } from '@shared/loggingShared'

export function LytHistorical({
  creationTimestamp,
  serverApiBaseUrl,
  restAPIKey,
  poolAddr,
  providerReadOnly,
}: {
  creationTimestamp: number | null
  serverApiBaseUrl?: string
  restAPIKey?: string
  poolAddr?: string
  providerReadOnly?: JsonRpcProvider
}): React.ReactElement {
  const [showAPY, setShowAPY] = useState(true)
  const [selectedTimeframe, setSelectedTimeframe] = useState<LytHistoryPeriod>(LytHistoryPeriod.OneMonth)
  const [xData, setXData] = useState<number[]>([])
  const [yData, setYData] = useState<number[]>([])
  const [dateFormatter, setDateFormatter] = useState<string>('MMM YY')
  const [isLoading, setIsLoading] = useState(false)

  const fetchData = (timeframe: LytHistoryPeriod, useAPY: boolean = showAPY) => {
    logOT.info('fetchData called with showAPY:', showAPY)
    setIsLoading(true)
    setSelectedTimeframe(timeframe)
    switch (timeframe) {
      case LytHistoryPeriod.OneMonth:
        setDateFormatter('DD MMM')
        break
      case LytHistoryPeriod.ThreeMonths:
        setDateFormatter('DD MMM')
        break
      case LytHistoryPeriod.SixMonths:
        setDateFormatter('MMM YY')
        break
      case LytHistoryPeriod.OneYear:
        setDateFormatter('MMM YY')
        break
      case LytHistoryPeriod.All:
        setDateFormatter('MMM YY')
        break
    }
    fetchLytHistory(
      timeframe,
      serverApiBaseUrl,
      restAPIKey,
      poolAddr,
      creationTimestamp ?? undefined,
      providerReadOnly,
      useAPY
    )
      .then((data) => {
        const sortedData = [...data].sort((a, b) => a.timestamp - b.timestamp)
        const maxPoints = 100 // or adjust based on timeframe
        const displayData = downsample(sortedData, maxPoints)

        setXData(displayData.map((item) => item.timestamp))
        setYData(displayData.map((item) => item.value))
        setIsLoading(false)
      })
      .catch((error) => {
        console.error('Error fetching data:', error)
        setIsLoading(false)
      })
  }

  const handleTimeframeClick = (timeframe: LytHistoryPeriod) => {
    fetchData(timeframe)
  }

  const handleDataTypeClick = (isAPY: boolean) => {
    setShowAPY(isAPY)
    fetchData(selectedTimeframe, isAPY)
  }

  useEffect(() => {
    if (serverApiBaseUrl && restAPIKey && poolAddr && creationTimestamp !== null && creationTimestamp > 0) {
      handleTimeframeClick(LytHistoryPeriod.OneMonth)
    }
  }, [serverApiBaseUrl, restAPIKey, poolAddr, creationTimestamp])
  return (
    <Box
      sx={{
        // backgroundColor: 'background.paper',
        padding: 3,
        borderRadius: 1,
        height: '400px',
        //...TODO_SX,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        Historical Performance
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: showAPY
              ? (theme) => (theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.primary.dark)
              : 'action.hover',
            color: showAPY ? 'primary.contrastText' : 'inherit',
            padding: '4px 12px',
            borderRadius: 1,
            cursor: 'pointer',
          }}
          onClick={() => handleDataTypeClick(true)}
        >
          <Typography variant="button">APY</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: !showAPY
              ? (theme) => (theme.palette.mode === 'light' ? theme.palette.primary.main : theme.palette.primary.dark)
              : 'action.hover',
            color: !showAPY ? 'primary.contrastText' : 'inherit',
            padding: '4px 12px',
            borderRadius: 1,
            cursor: 'pointer',
          }}
          onClick={() => handleDataTypeClick(false)}
        >
          <Typography variant="button">Exchange Rate</Typography>
        </Box>
      </Box>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 300,
          }}
        >
          <CircularProgress />
        </Box>
      ) : xData.length > 0 && yData.length > 0 ? (
        <LineChartPro
          xAxis={[
            {
              scaleType: 'point',
              data: xData,
              valueFormatter: (value: number) => dayjs(new Date(value * 1000)).format(dateFormatter),
            },
          ]}
          series={[
            {
              data: yData,
              valueFormatter: (value: number | null, context: any) => {
                const dateStr = dayjs(new Date(xData[context.dataIndex] * 1000)).format('YYYY-MM-DD')
                return `${dateStr}: ${value !== null ? value : '-'}`
              },
            },
          ]}
          height={300}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 300,
          }}
        >
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            No data available for this time period
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: 'row', justifyContent: 'flex-start' }}>
        {Object.values(LytHistoryPeriod).map((timeframe) => (
          <Box
            key={timeframe}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              padding: '4px 12px',
              borderRadius: 1,
              cursor: 'pointer',
              backgroundColor: selectedTimeframe === timeframe ? 'accent1.emphasis' : 'transparent',
              '&:hover': {
                backgroundColor: 'accent1.subtle',
              },
            }}
            onClick={() => handleTimeframeClick(timeframe)}
          >
            <Typography variant="button">{timeframe}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

function downsample(data: LytHistoryCTO[], maxPoints: number): LytHistoryCTO[] {
  if (data.length <= maxPoints) return data
  const step = Math.ceil(data.length / maxPoints)
  return data.filter((_: LytHistoryCTO, idx: number) => idx % step === 0)
}
